import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { useTenant } from "@/contexts/TenantContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { MessageSquare } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const { branding: globalBranding } = useBranding();
  const { tenant } = useTenant();
  const branding = tenant?.branding
    ? {
        ...globalBranding,
        logo_url: tenant.branding.logo_url || globalBranding.logo_url,
        accent_color: tenant.branding.accent_color || globalBranding.accent_color,
        primary_color: tenant.branding.accent_color || globalBranding.primary_color,
        app_name: tenant.branding.brand_name || globalBranding.app_name,
        register_title: tenant.branding.brand_name ? `Crea tu cuenta en ${tenant.branding.brand_name}` : globalBranding.register_title,
      }
    : globalBranding;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const result = await register(email, password, name);
    if (!result.success) {
      setError(result.error);
      setLoading(false);
      return;
    }
    // Forward to checkout if a plan was preselected, otherwise let PublicRoute send to /dashboard
    if (plan) navigate(`/dashboard/plans?plan=${encodeURIComponent(plan)}`, { replace: true });
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: branding.secondary_color || "#f1f5f9" }} data-testid="register-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="w-12 h-12 rounded-xl object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: branding.primary_color || "#0f172a" }}>
                <MessageSquare className="w-6 h-6" style={{ color: branding.accent_color || "#22c55e" }} />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl">Create account</CardTitle>
          <CardDescription>Get started with {branding.app_name || "your WhatsApp Bot Dashboard"}</CardDescription>
          {plan && (
            <div className="mt-3 inline-flex items-center gap-2 mx-auto px-3 py-1.5 rounded-full bg-amber-100 text-amber-900 text-xs font-medium" data-testid="register-preselected-plan">
              ✨ Plan preseleccionado: <strong className="uppercase">{plan}</strong>
            </div>
          )}
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-200" data-testid="register-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Min 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="register-password-input"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" style={{ backgroundColor: branding.primary_color || "#0f172a" }} disabled={loading} data-testid="register-submit-button">
              {loading ? "Creating account..." : "Create account"}
            </Button>
            <p className="text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to={plan ? `/login?plan=${encodeURIComponent(plan)}` : "/login"} className="text-[#0f172a] font-medium hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
